// Step 2 — the contractor's own documents. RON-5465.
// Prototype regions: `.doc-item`, `.upload-area`, `.upload-expiry`.
//
// AN UPLOAD IS NOT AN APPROVAL, and this screen must not let the two blur. §4: the three-call flow
// ends with the server recording a file it has confirmed is there — and the document then sits at
// `uploaded` with `scan: pending` until a scanner stamps it and a reviewer looks.
//
// So "Awaiting review" is a success state, drawn in neutral grey rather than red: the contractor
// has done their part and there is nothing more for them to do on that row. Only `rejected` is a
// problem, and it carries the reviewer's reason verbatim — that sentence is the only thing telling
// them what to fix.

import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  RENEWAL_STATE,
  explainsRenewalFirst,
  formatLongDate,
  getDocStateLabelKey,
  getDocTone,
  getUploadActionLabelKey,
  needsReplaceWarning,
  toAcceptAttribute,
} from "../portalConstants.js";

const TONE_TO_BADGE = { ok: "ok", pending: "pending", bad: "bad", todo: "bad" };
const TONE_TO_STATUS = { ok: "ok", pending: "pending", bad: "bad", todo: "bad" };

const UploadRow = ({ doc, isBusy, isActive, error, notice, onUpload }) => {
  const { t } = useTranslation("common");
  const fileRef = useRef(null);

  const [pendingFile, setPendingFile] = useState(null);
  const [expiry, setExpiry] = useState("");
  // AC-P-62/63. Replacing an APPROVED document withdraws the approval, so the warning comes
  // first and the upload area only opens once it has been accepted.
  //
  // Local and nothing more: choosing "Replace anyway" and then walking away leaves the document
  // exactly as it was, because nothing is sent until a file is chosen. That is AC-P-63, and it
  // is why this is a gate rather than a state change.
  const [replaceAccepted, setReplaceAccepted] = useState(false);

  const tone = getDocTone(doc.state);

  // WHETHER ANYTHING CAN BE DONE HERE IS THE SERVER'S ANSWER, not ours — Phase C, 22-October.
  //
  // This used to be `settled`, derived from `state`: approved and pending were treated as
  // finished and lost their upload box. Phase C reverses that — "any state can be replaced,
  // before or after submit, until the link closes" — and the rules are subtler than a state
  // check can carry. A `pending` document is replaceable but is NOT outstanding; an `approved`
  // one is replaceable but costs the contractor their clearance.
  //
  // So `can_upload` decides whether the area exists at all, and `upload_action` decides what
  // the button says. Deriving either would be the same mistake Phase 3 undid for the clearance
  // banner, one file away.
  const needsWarning = needsReplaceWarning(doc);
  // AC-P-80. On an approved document WITH an expiry the renewal explanation comes first and the
  // general warning second — they say opposite things about the same button, and a contractor
  // who reads only the general one will not renew a document they should.
  const renewalFirst = explainsRenewalFirst(doc);
  // The warning stands in front of the area; everything behind it waits for acceptance.
  const showUploadArea =
    doc.canUpload && (!(needsWarning || renewalFirst) || replaceAccepted);

  const meta = [
    doc.uploadedAt &&
      t("react_portal_uploaded_on").replace("%s", formatLongDate(doc.uploadedAt.slice(0, 10))),
    doc.expiry && t("react_portal_expires_on").replace("%s", formatLongDate(doc.expiry)),
    // AC-P-64. A re-upload makes a new document row, and this says which file it replaced —
    // otherwise a contractor who uploaded twice has no way to tell which one is on screen.
    // AC-P-77. In a renewal pack this is the document the pack was sent for; everything else is
    // shown but not offered.
    doc.needsRenewal &&
      doc.expiry &&
      t("react_portal_renewal_needed").replace("%s", formatLongDate(doc.expiry)),
    // AC-P-76 / AC-P-78. A renewal already uploaded. The second half is the one that matters:
    // the CURRENT version is still valid, so there is nothing for them to do yet.
    doc.renewal?.state === RENEWAL_STATE.AWAITING &&
      t("react_portal_renewal_awaiting").replace(
        "%s",
        formatLongDate(doc.renewal.stillValidUntil)
      ),
    doc.renewal?.state === RENEWAL_STATE.NEEDS_ACTION &&
      t("react_portal_renewal_rejected").replace(
        "%s",
        formatLongDate(doc.renewal.stillValidUntil)
      ),
    doc.replacesUploadedAt &&
      t("react_portal_replaces_file").replace(
        "%s",
        formatLongDate(String(doc.replacesUploadedAt).slice(0, 10))
      ),
    !doc.uploadedAt && doc.description,
  ]
    .filter(Boolean)
    .join(" · ");

  const onPick = (event) => {
    const [file] = event.target.files || [];
    if (!file) return;

    // Held rather than sent, when an expiry is required: the server refuses the third call without
    // one (422), and asking after the upload has already gone would be asking twice.
    if (doc.expiryRequired) {
      setPendingFile(file);

      return;
    }

    onUpload(doc, file, null);
  };

  return (
    <div className="portal-doc">
      <div className="portal-doc__header">
        <span
          className={`portal-doc__status portal-doc__status--${TONE_TO_STATUS[tone]}`}
          aria-hidden="true"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {tone === "ok" ? (
              <polyline points="20 6 9 17 4 12" />
            ) : tone === "pending" ? (
              <circle cx="12" cy="12" r="4" />
            ) : (
              <>
                <line x1="12" y1="8" x2="12" y2="13" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </>
            )}
          </svg>
        </span>

        <div className="portal-doc__info">
          <p className="portal-doc__title">{doc.title}</p>
          {meta && <p className="portal-doc__meta">{meta}</p>}

          <span className={`portal-doc__badge portal-doc__badge--${TONE_TO_BADGE[tone]}`}>
            {t(getDocStateLabelKey(doc.state))}
          </span>

          {/* The reviewer's own sentence. Shown verbatim because it is the only thing that says
              what to do next — summarising it would cost the contractor another round trip. */}
          {doc.rejectedReason && (
            <p className="portal-doc__meta" style={{ marginTop: 6 }}>
              {doc.rejectedReason}
            </p>
          )}
        </div>
      </div>

      {/* AC-P-62. Three consequences, and none is undone by pressing Cancel later: the approval
          is withdrawn, they are not cleared until the new file is approved, and they cannot
          check in if it is not approved by the time they arrive. So it is said before the area
          opens, with a way out that changes nothing.

          AC-P-80 adds the renewal case ABOVE it, which is the opposite message: a later expiry
          keeps the current document valid and costs them nothing. */}
      {doc.canUpload && (needsWarning || renewalFirst) && !replaceAccepted && (
        <div className="portal-replace">
          {renewalFirst && (
            <p className="portal-replace__renewal">
              {t("react_portal_renewal_explain").replace(
                "%s",
                formatLongDate(doc.renewalValidUntil)
              )}
            </p>
          )}
          {needsWarning && (
            <p className="portal-replace__warn">{t("react_portal_replace_warning")}</p>
          )}
          <div className="portal-replace__actions">
            <button
              type="button"
              className="portal-replace__keep"
              onClick={() => setReplaceAccepted(false)}
              disabled={isBusy}
            >
              {t("react_portal_replace_keep")}
            </button>
            <button
              type="button"
              className="portal-replace__go"
              onClick={() => setReplaceAccepted(true)}
              disabled={isBusy}
            >
              {t("react_portal_replace_anyway")}
            </button>
          </div>
        </div>
      )}

      {showUploadArea && (
        <>
          {/* ABOVE THE UPLOAD AREA, not at the top of the page.
              Step 2 sits a long way down, and a contractor who has just pressed upload is
              looking here. A banner at the top is an answer to a question they can no longer
              see — on a phone it is most of a screenful away.

              Only for the row the write actually belongs to: `isActive` is the document id the
              write named, so two rows cannot both claim the same sentence. */}
          {isActive && !isBusy && (error || notice) && (
            <p
              className={`portal-upload__msg portal-upload__msg--${error ? "bad" : "ok"}`}
              role={error ? "alert" : "status"}
            >
              {error || notice}
            </p>
          )}

          <input
            ref={fileRef}
            type="file"
            accept={toAcceptAttribute(doc.acceptedFileTypes)}
            onChange={onPick}
            style={{ display: "none" }}
          />

          <button
            type="button"
            className={`portal-upload${pendingFile ? " portal-upload--done" : ""}`}
            onClick={() => fileRef.current?.click()}
            disabled={isBusy}
            aria-busy={isActive && isBusy}
          >
            {/* An upload is three calls and a file going over a phone connection, so it is the
                one action on this page with a wait worth marking. Without it the area simply
                greys out and nothing else happens, which reads as a dead button — that is what
                "the upload area is not clickable" turned out to be the first time.

                It replaces the icon rather than sitting beside it, so the area does not change
                height and the page does not jump under the reader's thumb. */}
            <span className="portal-upload__icon" aria-hidden="true">
              {isActive && isBusy ? (
                <span className="portal-upload__spinner" />
              ) : (
              <svg
                width="17"
                height="17"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
              )}
            </span>

            {isActive && isBusy ? (
              <p className="portal-upload__title">{t("react_portal_uploading")}</p>
            ) : pendingFile ? (
              <p className="portal-upload__file">{pendingFile.name}</p>
            ) : (
              <p className="portal-upload__title">
                {/* "Upload corrected document", "Replace file", "Replace document", "Upload
                    renewed document" — CHANGES §1, keyed on the server's `upload_action`. The
                    word matters: "Replace document" on an approved file is not the same act as
                    "Upload" on an empty one, and the button is the last thing read before it
                    happens. */}
                {t(getUploadActionLabelKey(doc.uploadAction)).replace("%s", doc.title)}
              </p>
            )}
            <p className="portal-upload__sub">
              {t("react_portal_upload_sub").replace(
                "%s",
                String(doc.acceptedFileTypes || "").toUpperCase().split(",").join(", ")
              )}
            </p>
          </button>

          {/* Asked only once a file is chosen, and only where the server requires it. The expiry
              goes on the third call; without it that call is a 422. */}
          {pendingFile && doc.expiryRequired && (
            <div className="portal-upload__expiry">
              <label htmlFor={`expiry-${doc.id}`}>{t("react_portal_expiry_label")}</label>
              <input
                id={`expiry-${doc.id}`}
                type="date"
                value={expiry}
                onChange={(event) => setExpiry(event.target.value)}
              />
              <div className="portal-sign__row">
                <button
                  type="button"
                  className="portal-sign__btn"
                  disabled={!expiry || isBusy}
                  onClick={() => {
                    onUpload(doc, pendingFile, expiry);
                    setPendingFile(null);
                    setExpiry("");
                    // Closed again behind the warning. The reply carries a NEW id for this
                    // document (Phase B), so the card is about to be replaced wholesale — but
                    // if the upload fails, the gate should be shut rather than left open.
                    setReplaceAccepted(false);
                  }}
                >
                  {t("react_portal_send_document")}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PortalUploads = ({
  customer,
  documents,
  isBusy,
  onUpload,
  // The row a write is running on, and what to say about it. One row at a time: every control
  // on the page is disabled while a write is in flight, so there can only ever be one.
  activeDocId = null,
  error = null,
  notice = null,
}) => {
  const { t } = useTranslation("common");

  return (
    <>
      <p className="portal-section__intro">
        {t("react_portal_upload_intro").replace("%s", customer || "")}
      </p>

      {documents.map((doc) => (
        <UploadRow
          key={doc.id}
          doc={doc}
          isBusy={isBusy}
          isActive={doc.id === activeDocId}
          error={error}
          notice={notice}
          onUpload={onUpload}
        />
      ))}
    </>
  );
};

export default PortalUploads;
